import * as THREE from 'three';

// Scratch objects used by projectBackHitToNdc — avoids per-call allocations.
const _invRay  = new THREE.Vector3();
const _invClip = new THREE.Vector4();

// Project the back-hemisphere exit hit of the ray from camera->frontPoint
// into NDC. Returns false if the ray/clip is invalid.
export function projectBackHitToNdc(
  frontPoint: THREE.Vector3,
  camPos: THREE.Vector3,
  viewProj: THREE.Matrix4,
  outNdc: THREE.Vector2,
): boolean {
  _invRay.copy(frontPoint).normalize().sub(camPos);
  const denom = _invRay.lengthSq();
  if (denom <= 1e-8) return false;
  const t = (camPos.lengthSq() - 1.0) / denom;
  if (t <= 0.0) return false;

  _invClip
    .set(
      camPos.x + _invRay.x * t,
      camPos.y + _invRay.y * t,
      camPos.z + _invRay.z * t,
      1.0,
    )
    .applyMatrix4(viewProj);

  if (_invClip.w <= 1e-8) return false;
  outNdc.set(_invClip.x / _invClip.w, _invClip.y / _invClip.w);
  return true;
}

// Converts a world-space point on the unit sphere to equirectangular UV [0,1]².
// Matches the getHitData formula exactly so UV-space stamp placement is consistent.
export function worldPointToUV(p: THREE.Vector3): THREE.Vector2 {
  const n = p.clone().normalize();
  const phi = Math.atan2(n.z, -n.x);
  const u = phi < 0 ? 1 + phi / (2 * Math.PI) : phi / (2 * Math.PI);
  const v = Math.acos(THREE.MathUtils.clamp(n.y, -1, 1)) / Math.PI;
  return new THREE.Vector2(u, v);
}

// Inverse of worldPointToUV: equirectangular UV [0,1]² → unit-sphere world point.
// Matches THREE.SphereGeometry's UV convention exactly (inverse of getHitData formula).
export function uvToWorldPoint(u: number, v: number): THREE.Vector3 {
  const phi   = u * 2 * Math.PI;
  const theta = v * Math.PI;
  return new THREE.Vector3(
    -Math.cos(phi) * Math.sin(theta),
     Math.cos(theta),
     Math.sin(phi) * Math.sin(theta),
  );
}

/**
 * Returns an orthonormal tangent frame {sRight, sUp} aligned with the
 * equirectangular UV grid at point P on the unit sphere.
 *
 *  sRight — geographic east: tangent in the direction of increasing longitude (U).
 *  sUp    — geographic north: tangent in the direction of decreasing latitude (V),
 *            i.e. toward the north pole.
 *
 * Used by the UV-panel stamp / text tool so stamps stay upright relative to
 * the equirectangular map regardless of where the 3-D camera is pointing.
 *
 * Derivation (uvToWorldPoint convention: P = (−cos φ·sin θ, cos θ, sin φ·sin θ)):
 *   sRight = dP/dφ / |dP/dφ| = (sin φ, 0, −(−cos φ)) … wait we need +U direction:
 *   Actually dP/du direction (increasing U → increasing phi):
 *     = (sin φ · sin θ, 0, cos φ · sin θ) / sin θ = (sin φ, 0, cos φ)
 *     But uvToWorldPoint uses phi = u*2PI so the longitudinal east tangent at P is:
 *     sRight = (pz/sinTheta, 0, −px/sinTheta)  where sinTheta = √(px²+pz²)
 *   sUp (geographic north, −dP/dv direction):
 *     = (−px·py, px²+pz², −pz·py) / sinTheta
 */
export function uvAlignedTangentFrame(P: THREE.Vector3): { sRight: THREE.Vector3; sUp: THREE.Vector3 } {
  const px = P.x, py = P.y, pz = P.z;
  const sinTheta = Math.sqrt(px * px + pz * pz);
  if (sinTheta > 0.001) {
    return {
      sRight: new THREE.Vector3(pz, 0, -px).divideScalar(sinTheta),
      sUp:    new THREE.Vector3(-px * py, px * px + pz * pz, -pz * py).divideScalar(sinTheta),
    };
  }
  // At poles the longitude direction is arbitrary; use a stable fallback.
  return {
    sRight: new THREE.Vector3(0, 0, 1),
    sUp:    new THREE.Vector3(py > 0 ? -1 : 1, 0, 0),
  };
}

// Returns the frustum half-height at a given camera-space depth.
// For perspective cameras: depth * tan(fovY/2).  For orthographic: (top − bottom) / 2.
export function camFrustumHalfH(cam: THREE.Camera, depth: number): number {
  if (cam instanceof THREE.OrthographicCamera) {
    return (cam.top - cam.bottom) / 2;
  }
  return depth * Math.tan(
    THREE.MathUtils.degToRad((cam as THREE.PerspectiveCamera).fov) / 2
  );
}

/** Returns one entry per copy: the rotated sphere center plus the quaternion
 *  used to rotate it.  The first entry is always the original (identity q). */
export function getDuplicationCenters(
  baseCenter: THREE.Vector3,
  dupMode: string,
): Array<{ center: THREE.Vector3; quaternion: THREE.Quaternion }> {
  const identity = new THREE.Quaternion();
  const result: Array<{ center: THREE.Vector3; quaternion: THREE.Quaternion }> = [
    { center: baseCenter.clone(), quaternion: identity.clone() },
  ];
  const build = (angleDeg: number) => {
    const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), THREE.MathUtils.degToRad(angleDeg));
    result.push({ center: baseCenter.clone().applyQuaternion(q), quaternion: q.clone() });
  };
  switch (dupMode) {
    case 'diptych':    build(180); break;
    case 'triptych':   build(120); build(240); break;
    case 'quadriptych': build(90); build(180); build(270); break;
    default: break;
  }
  return result;
}

/**
 * Returns the geodesic diameter (radians) for wrap-mode stamps that produces
 * the same visual screen size as projection mode at the current camera distance.
 *
 * Derived by solving: sin(θ₀) / (D − cos(θ₀)) = stampSize
 * Closed form: θ₀ = asin(s·D / √(1+s²)) − atan(s)   [diameter = 2θ₀]
 */
export function wrapStampDiam(D: number, stampSize: number): number {
  const s = stampSize;
  const arg = Math.min(1, s * D / Math.sqrt(1 + s * s));
  return 2 * (Math.asin(arg) - Math.atan(s));
}
